import { useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, 
  FileText, 
  MessageSquare, 
  Settings,
  Brain,
  Menu,
  Home
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { LinkLogo } from '@/components/ui/LinkLogo';

const primaryNavItems = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/claims', label: 'Claims', icon: FileText },
  { path: '/concierge', label: 'Concierge', icon: MessageSquare },
];

const secondaryNavItems = [
  { path: '/payer-intelligence', label: 'Payer Intelligence', icon: Brain },
  { path: '/settings', label: 'Settings', icon: Settings },
];

interface MobileNavProps {
  className?: string;
}

export function MobileBottomNav({ className }: MobileNavProps) {
  const location = useLocation();
  
  return (
    <nav className={cn(
      "fixed bottom-0 left-0 right-0 z-50 bg-sidebar/95 backdrop-blur-xl border-t border-sidebar-border",
      "flex items-center justify-around py-2 px-4",
      "safe-area-pb",
      className
    )}>
      {primaryNavItems.map(({ path, label, icon: Icon }) => {
        const isActive = location.pathname === path;
        return (
          <NavLink
            key={path}
            to={path}
            className={cn(
              "flex flex-col items-center gap-1 py-2 px-4 rounded-lg transition-all duration-200 min-h-[56px] min-w-[64px]",
              isActive 
                ? "text-primary" 
                : "text-muted-foreground hover:text-foreground"
            )}
          >
            <Icon className={cn(
              "w-5 h-5 transition-all",
              isActive && "scale-110"
            )} />
            <span className="text-[10px] font-medium">{label}</span>
            {isActive && (
              <div className="absolute -bottom-0.5 w-8 h-0.5 rounded-full bg-primary" />
            )}
          </NavLink>
        );
      })}
    </nav>
  );
}

export function MobileHamburgerMenu() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogoClick = () => {
    navigate('/');
    setOpen(false);
  };

  const handleNavClick = () => {
    setOpen(false);
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button className="p-2 rounded-lg hover:bg-muted/50 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center">
          <Menu className="w-5 h-5" />
        </button>
      </SheetTrigger>
      <SheetContent side="right" className="w-[280px] bg-sidebar border-l border-sidebar-border p-0">
        <div className="flex flex-col h-full">
          {/* Header with Full Logo (icon + text) */}
          <div className="p-4 border-b border-sidebar-border">
            <button 
              onClick={handleLogoClick}
              className="flex items-center gap-3 group cursor-pointer transition-all duration-200 hover:opacity-80"
            >
              <LinkLogo size="md" showDomain={true} />
            </button>
          </div>

          {/* Home Link */}
          <div className="p-2">
            <NavLink
              to="/"
              onClick={handleNavClick}
              className={cn(
                "flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 min-h-[48px]",
                location.pathname === '/'
                  ? "bg-sidebar-accent text-sidebar-accent-foreground" 
                  : "text-sidebar-foreground hover:bg-sidebar-accent/50"
              )}
            >
              <Home className="w-5 h-5" />
              <span className="font-medium">Home</span>
            </NavLink>
          </div>

          {/* Primary Nav */}
          <div className="flex-1 py-2 px-2 space-y-1">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground px-4 py-2">
              Main Navigation
            </p>
            {primaryNavItems.map(({ path, label, icon: Icon }) => {
              const isActive = location.pathname === path;
              return (
                <NavLink
                  key={path}
                  to={path}
                  onClick={handleNavClick}
                  className={cn(
                    "flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 min-h-[48px]",
                    isActive 
                      ? "bg-sidebar-accent text-sidebar-accent-foreground" 
                      : "text-sidebar-foreground hover:bg-sidebar-accent/50"
                  )}
                >
                  <Icon className={cn(
                    "w-5 h-5",
                    isActive ? "text-primary" : "text-muted-foreground"
                  )} />
                  <span className="font-medium">{label}</span>
                </NavLink>
              );
            })}
          </div>

          {/* Secondary Nav */}
          <div className="py-2 px-2 border-t border-sidebar-border">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground px-4 py-2">
              More
            </p>
            {secondaryNavItems.map(({ path, label, icon: Icon }) => {
              const isActive = location.pathname === path;
              return (
                <NavLink
                  key={path}
                  to={path}
                  onClick={handleNavClick}
                  className={cn(
                    "flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 min-h-[48px]",
                    isActive 
                      ? "bg-sidebar-accent text-sidebar-accent-foreground" 
                      : "text-sidebar-foreground hover:bg-sidebar-accent/50"
                  )}
                >
                  <Icon className={cn(
                    "w-5 h-5",
                    isActive ? "text-primary" : "text-muted-foreground"
                  )} />
                  <span className="font-medium">{label}</span>
                </NavLink>
              );
            })}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}